import { useState, useMemo, useRef, useCallback } from 'react';
import { Provider } from 'react-redux';
import { createStore } from '../../redux/createStore';
import { StoreContext } from '../../redux/storeContext';
import { EmptyComponent } from '../utils/EmptyComponent';
import { DEFAULT_PAGE_SIZES } from '../../pagination/constants/pageSizes';
import emptyFunction from 'react-utils/emptyFunction';
import Table from './Table';
import { ProvideAuth } from '../../auth/context/AuthContext';
import { COLUMN_MINIMUM_WIDTH, DEFAULT_COLUMN_WIDTH } from '../constants/sizes';
import { DefaultRowComponent } from './DefaultRowComponent';
import { DefaultTableComponent } from './DefaultTableComponent';
import { ProvideLogger } from '../../logger/context/LoggerContext';
import { ProvideFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import DefaultSelectAllComponent from '../../selection/components/DefaultSelectAllComponent';
import { DnDProviderCompat } from '../../dndCompat/DnDProviderCompat';
import { DnDHOCCompat } from '../../dndCompat/DnDHOCCompat';
import { ProvideVirtualization } from '../../virtualization/VirtualizationContext';
import { getColumnKey } from '../utils/getColumnKey';
import Raven from 'raven-js';
import { BulkActionsContainerAboveTable } from './BulkActionsContainerAboveTable';
import { ReorderingContextProvider } from '../../reordering/core/ReorderingContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const emptyArray = [];
const DEFAULT_NAMESPACE = 'DEFAULT_NAMESPACE';
const TableWrapper = ({
  alternateHeaderLeftSlot = null,
  BulkActionsComponent = EmptyComponent,
  canSelectAllRecords = true,
  columns,
  CustomSelectAllComponent = DefaultSelectAllComponent,
  disableScrollReset = false,
  getRowId,
  hasGate,
  hasScope,
  headerLeftSlot = null,
  headerRightSlot = null,
  isBulkActionsRedesignEnabled = false,
  isIndexRedesignEnabled = false,
  loading = false,
  logger = emptyFunction,
  noRowsSlot = null,
  numberOfFrozenColumns = 0,
  onColumnOrderChange = emptyFunction,
  onColumnWidthChange = emptyFunction,
  // do not default to empty function, as the undefined case falls back to the default behavior
  contextMenuConfig,
  onPageIndexChange = emptyFunction,
  onPageSizeChange = emptyFunction,
  onSortsChange = emptyFunction,
  pageable = true,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZES[0],
  pageSizes = DEFAULT_PAGE_SIZES,
  reorderable = true,
  resizable = true,
  rows,
  RowComponent = DefaultRowComponent,
  selectable = true,
  sortable = true,
  sorts = emptyArray,
  TableComponent = DefaultTableComponent,
  totalRowCount = 0,
  zebraStripe = false,
  condensed = true
}, __ref) => {
  // This ensures that the reference is stable as long as the component is mounted.
  // useMemo may throw away a reference in the future so it should not be used here.
  const [store] = useState(() => createStore({
    initialWidths: columns.reduce((acc, column) => {
      const width = column.width || DEFAULT_COLUMN_WIDTH;
      acc[getColumnKey(column.columnNamespace || DEFAULT_NAMESPACE, column.columnName)] = width;
      return acc;
    }, {})
  }));
  const alreadyLoggedRavenRef = useRef(false);
  const columnsWithDefaults = useMemo(() => columns.map(column => {
    //we explicitly prevent a column width of 0 - this || is weight bearing over ??
    const minimumColumnWidth = column.minimumColumnWidth || COLUMN_MINIMUM_WIDTH;
    if (column.width && column.width < minimumColumnWidth) {
      const errorMessage = 'FrameworkDataTable has been given conflicting column width information. We are falling back to a sane fallback, but this will cause column rendering and resizing to be extremely glitchy for users.';
      const errorExtra = {
        columnName: column.columnName,
        columnNamespace: column.columnNamespace,
        width: column.width,
        minimumColumnWidth: column.minimumColumnWidth
      };
      console.error(errorMessage, errorExtra);
      if (!alreadyLoggedRavenRef.current) {
        Raven.captureException(
        //REF: https://git.hubteam.com/HubSpot/Critsit/issues/4542
        //Do not make this error at runtime, it has caused a critsit. This should not get out to prod, but if it does we want to gracefully degrade instead of blow up
        new Error(errorMessage), {
          extra: {
            errorExtra
          }
        });
        alreadyLoggedRavenRef.current = true;
      }
    }
    const width = Math.max(column.width || DEFAULT_COLUMN_WIDTH, minimumColumnWidth);
    return Object.assign({}, column, {
      columnNamespace: column.columnNamespace || DEFAULT_NAMESPACE,
      width,
      minimumColumnWidth,
      // The single != is load-bearing! We do not want to override column-level settings.
      resizable: column.resizable != null ? column.resizable : resizable,
      reorderable: column.reorderable != null ? column.reorderable : reorderable,
      sortable: column.sortable != null ? column.sortable : sortable
    });
  }), [columns, reorderable, resizable, sortable]);
  const sortsWithDefaults = useMemo(() => sorts.map(sort => Object.assign({}, sort, {
    columnNamespace: sort.columnNamespace != null ? sort.columnNamespace : DEFAULT_NAMESPACE
  })), [sorts]);
  const shouldShowBulkActionsContainerAboveTable = isBulkActionsRedesignEnabled && isIndexRedesignEnabled;
  const onItemOrderChange = useCallback((fromIndex, toIndex) => {
    const column = columnsWithDefaults[fromIndex];
    if (column) {
      onColumnOrderChange({
        columnNamespace: column.columnNamespace,
        columnName: column.columnName,
        nextIndex: toIndex
      });
    }
  }, [columnsWithDefaults, onColumnOrderChange]);
  return /*#__PURE__*/_jsx(ProvideAuth, {
    hasScope: hasScope,
    hasGate: hasGate,
    children: /*#__PURE__*/_jsx(ProvideLogger, {
      logger: logger,
      children: /*#__PURE__*/_jsx(ProvideVirtualization, {
        children: /*#__PURE__*/_jsx(ProvideFDTContextMenuConfig, {
          contextMenuConfig: contextMenuConfig,
          children: /*#__PURE__*/_jsx(Provider, {
            store: store,
            context: StoreContext,
            children: /*#__PURE__*/_jsxs(DnDProviderCompat, {
              children: [shouldShowBulkActionsContainerAboveTable && /*#__PURE__*/_jsx(BulkActionsContainerAboveTable, {
                BulkActionsComponent: BulkActionsComponent,
                canSelectAllRecords: canSelectAllRecords,
                getRowId: getRowId,
                isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
                isIndexRedesignEnabled: isIndexRedesignEnabled,
                rows: rows,
                totalRowCount: totalRowCount
              }), /*#__PURE__*/_jsx(ReorderingContextProvider, {
                onItemOrderChange: onItemOrderChange,
                orientation: "horizontal",
                children: /*#__PURE__*/_jsx(Table, {
                  alternateHeaderLeftSlot: alternateHeaderLeftSlot,
                  BulkActionsComponent: BulkActionsComponent,
                  canSelectAllRecords: canSelectAllRecords,
                  columns: columnsWithDefaults,
                  condensed: condensed,
                  CustomSelectAllComponent: CustomSelectAllComponent,
                  disableScrollReset: disableScrollReset,
                  getRowId: getRowId,
                  headerLeftSlot: headerLeftSlot,
                  headerRightSlot: headerRightSlot,
                  isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
                  isIndexRedesignEnabled: isIndexRedesignEnabled,
                  loading: loading,
                  noRowsSlot: noRowsSlot,
                  numberOfFrozenColumns: numberOfFrozenColumns,
                  onColumnOrderChange: onColumnOrderChange,
                  onColumnWidthChange: onColumnWidthChange,
                  onPageIndexChange: onPageIndexChange,
                  onPageSizeChange: onPageSizeChange,
                  onSortsChange: onSortsChange,
                  pageable: pageable,
                  pageIndex: pageIndex,
                  pageSize: pageSize,
                  pageSizes: pageSizes,
                  reorderable: reorderable,
                  resizable: resizable,
                  RowComponent: RowComponent,
                  rows: rows,
                  selectable: selectable,
                  sorts: sortsWithDefaults,
                  sortable: sortable,
                  TableComponent: TableComponent,
                  totalRowCount: totalRowCount,
                  zebraStripe: zebraStripe
                })
              })]
            })
          })
        })
      })
    })
  });
};
export default DnDHOCCompat(TableWrapper);